import { InfiniteScroll } from "@/components/InfiniteScroll";
import { StudentCard } from "@/components/cards/StudentCard";
import { BaseToolbar } from "@/components/ui/base-toolbar";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useDebounce } from "use-debounce";

import { Student } from "@/services/admin/common.interfaces";
import { getStudentById, updateStudent, fetchStudents } from "@/services/admin/student.service";

interface StudentWithLockStatus extends Student {
  isLocked?: boolean;
}

const StudentManagementPage: React.FC = () => {
  const [resetKey, setResetKey] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText] = useDebounce(searchText, 1000);
  const navigate = useNavigate();

  const handleEdit = async (studentId: string) => {
    try {
      const student = await getStudentById(studentId);
      navigate(`/admin/access/students/${studentId}`, { state: { student } });
    } catch (error) {
      console.error("Failed to fetch student:", error);
      toast.error("Failed to fetch student. Please try again.");
    }
  };

  const handleToggleLock = async (studentId: string) => {
    try {
      const student = await getStudentById(studentId);
      await updateStudent(studentId, { isLocked: !student.isLocked });
      toast.success(`Student ${student.isLocked ? 'unlocked' : 'locked'} successfully.`);
      setResetKey((prev) => prev + 1); // Refresh the list
    } catch (error) {
      console.error("Failed to update student access:", error);
      toast.error("Failed to update student access. Please try again.");
    }
  };

  // Reset InfiniteScroll quando o texto de busca debounced mudar
  useEffect(() => {
    setResetKey((prev) => prev + 1);
  }, [debouncedSearchText]);

  const fetchStudentsPage = useCallback(async (page: number): Promise<StudentWithLockStatus[]> => {
    try {
      const students = await fetchStudents(page, 10, debouncedSearchText);
      // Level test data now comes from the backend pagination
      return students;
    } catch (error) {
      console.error("Failed to fetch students:", error);
      toast.error("Failed to load students. Please try again.");
      return [];
    }
  }, [debouncedSearchText]);

  const renderItem = (student: StudentWithLockStatus, key: React.Key) => (
    <StudentCard 
      key={key}
      student={{
        name: student.name || 'Nome não disponível',
        email: student.email || 'Email não disponível',
        status: student.status || 'Status não disponível',
        enrollmentDate: student.enrollmentDate || 'Data não disponível',
        progress: student.progress || { packagesCount: 0, overallProgress: 0 },
        levelTest: student.levelTest
      }}
      studentId={student.id}
      onEdit={() => handleEdit(student.id)}
      onToggleLock={() => handleToggleLock(student.id)}
      isLocked={student.isLocked}
      className="mb-4"
    />
  );

  const itemKey = (student: Student) => student.id;

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  return (
    <AdminPageLayout title="Student Management">
      <main id="main" className="py-6">
        <BaseToolbar
          searchPlaceholder="Search students..."
          searchValue={searchText}
          onCreateClick={() => navigate('/admin/access/students/new')}
          onSearchChange={handleSearch}
        />

        <InfiniteScroll<Student>
          fetchItems={fetchStudentsPage}
          renderItem={renderItem}
          itemKey={itemKey}
          layout="grid"
          gridCols={{ default: 2, lg: 3 }}
          key={resetKey}
        />
      </main>
    </AdminPageLayout>
  );
};

export default StudentManagementPage;
